import {
  useEffect,
  useEffectEvent,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { cx } from '@/core/utils';
import { Icon } from './Icon';

/** A held stepper button starts repeating after this long, then steps this often, ms. */
const REPEAT_DELAY = 400;
const REPEAT_INTERVAL = 70;
/** Steps are committed together once they pause this long, ms (longer than REPEAT_DELAY). */
const STEP_COMMIT_DELAY = 500;
/** While dragging, the value is committed once the thumb rests this long, ms. */
const DRAG_COMMIT_DELAY = 200;

interface Props {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  /** Called when the user settles on a value: the thumb rests or is released, or −/+ taps stop. */
  onCommit: (value: number) => void;
  /** Accessible names of the −/+ buttons, e.g. "Decrease glide ratio". */
  decreaseLabel: string;
  increaseLabel: string;
  /** On the outer element: a column of the label row and the slider row, e.g. `gap-1` to space them. */
  className?: string;
}

// The range input, restyled: a thin track filled up to the thumb and a large thumb (28px on phones).
// --p is the position (0–1); the fill ends at the thumb's centre, which travels the track minus its width.
// Utilities are spelled out per pseudo-element: Tailwind only generates classes it finds written in full.
const RANGE = cx(
  'h-10 min-w-0 flex-1 cursor-pointer appearance-none bg-transparent focus-visible:outline-none md:h-8',
  '[--thumb:28px] md:[--thumb:20px] [--fill:calc(var(--thumb)/2+(100%-var(--thumb))*var(--p))]',
  '[--track:linear-gradient(to_right,var(--color-accent)_var(--fill),var(--color-neutral-300)_var(--fill))]',
  '[&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-(image:--track)',
  '[&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-(image:--track)',
  '[&::-webkit-slider-thumb]:size-(--thumb) [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-neutral-300 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_1px_4px_rgba(0,0,0,.25)] motion-safe:[&::-webkit-slider-thumb]:transition-transform',
  '[&::-moz-range-thumb]:size-(--thumb) [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-neutral-300 [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow-[0_1px_4px_rgba(0,0,0,.25)] motion-safe:[&::-moz-range-thumb]:transition-transform',
  // WebKit puts the thumb's top on the track's top.
  '[&::-webkit-slider-thumb]:mt-[calc((6px-var(--thumb))/2)]',
  'active:[&::-webkit-slider-thumb]:scale-110 active:[&::-moz-range-thumb]:scale-110',
  'focus-visible:[&::-webkit-slider-thumb]:outline-2 focus-visible:[&::-webkit-slider-thumb]:outline-offset-2 focus-visible:[&::-webkit-slider-thumb]:outline-accent',
  'focus-visible:[&::-moz-range-thumb]:outline-2 focus-visible:[&::-moz-range-thumb]:outline-offset-2 focus-visible:[&::-moz-range-thumb]:outline-accent',
);

const STEPPER =
  'inline-flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full border border-neutral-300 bg-white text-neutral-700 select-none hover:bg-neutral-100 active:bg-neutral-200 disabled:cursor-default disabled:opacity-40 md:size-8';

const decimalsOf = (step: number) => (String(step).split('.')[1] ?? '').length;

interface StepperProps {
  label: string;
  disabled: boolean;
  /** Moves the value one step; false when it could not move (at the end of the range). */
  onStep: () => boolean;
  children: ReactNode;
}

/** −/+ button: steps on press and repeats while held; keyboards activate it with a click (detail 0). */
function Stepper({ label, disabled, onStep, children }: StepperProps) {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const repeat = (delay: number) => {
    timer.current = setTimeout(() => onStep() && repeat(REPEAT_INTERVAL), delay);
  };
  const stop = () => clearTimeout(timer.current);

  return (
    <button
      type="button"
      className={STEPPER}
      aria-label={label}
      disabled={disabled}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        clearTimeout(timer.current);
        if (onStep()) repeat(REPEAT_DELAY);
      }}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      onClick={(e) => e.detail === 0 && onStep()}
      // A long press would otherwise open the context menu or select text on touch screens.
      onContextMenu={(e) => e.preventDefault()}
    >
      <Icon className="size-4">{children}</Icon>
    </button>
  );
}

/**
 * A labelled slider for a number in a range, showing its value as it moves. The thumb is large enough
 * to grab on a phone, and −/+ buttons (hold to repeat) step precisely where a finger can't.
 * It reports values debounced, so costly work follows a drag without running on every move.
 */
export function Slider({
  label,
  value,
  min,
  max,
  step,
  onCommit,
  decreaseLabel,
  increaseLabel,
  className,
}: Props) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  /** The value being chosen, until it is committed. */
  const [draft, setDraft] = useState<number | null>(null);
  const draftRef = useRef<number | null>(null);
  const commitTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const decimals = decimalsOf(step);
  const shown = draft ?? value;

  const update = (v: number | null) => {
    draftRef.current = v;
    setDraft(v);
  };

  const commit = () => {
    clearTimeout(commitTimer.current);
    const v = draftRef.current;
    if (v === null) return;
    update(null);
    if (v !== value) onCommit(v);
  };

  // The native `change` fires when the thumb is released (and per key press); React's onChange on every move.
  const onRelease = useEffectEvent((v: number) => {
    update(v);
    commit();
  });
  useEffect(() => {
    const el = inputRef.current!;
    const onChange = () => onRelease(el.valueAsNumber);
    el.addEventListener('change', onChange);
    return () => {
      el.removeEventListener('change', onChange);
      clearTimeout(commitTimer.current);
    };
  }, []);

  /** Shows `v` now and commits it once nothing else happens for `delay` ms. */
  const commitLater = (v: number, delay: number) => {
    update(v);
    clearTimeout(commitTimer.current);
    commitTimer.current = setTimeout(commit, delay);
  };

  const stepBy = (direction: 1 | -1) => {
    const current = draftRef.current ?? value;
    const next = +Math.min(max, Math.max(min, current + direction * step)).toFixed(decimals);
    // Taps in quick succession, or a held button, are committed together.
    commitLater(next, STEP_COMMIT_DELAY);
    return next !== current;
  };

  return (
    <div className={cx('flex flex-col py-1.5', className)}>
      <div className="flex min-h-9 items-center justify-between gap-3 md:min-h-7">
        <label htmlFor={id}>{label}</label>
        <output htmlFor={id} className="font-semibold tabular-nums">
          {shown.toFixed(decimals)}
        </output>
      </div>
      <div className="flex items-center gap-3 md:gap-2">
        <Stepper label={decreaseLabel} disabled={shown <= min} onStep={() => stepBy(-1)}>
          <path d="M5 12h14" />
        </Stepper>
        <input
          ref={inputRef}
          id={id}
          type="range"
          min={min}
          max={max}
          step={step}
          value={shown}
          onChange={(e) => commitLater(e.target.valueAsNumber, DRAG_COMMIT_DELAY)}
          className={RANGE}
          style={{ '--p': (shown - min) / (max - min) } as CSSProperties}
        />
        <Stepper label={increaseLabel} disabled={shown >= max} onStep={() => stepBy(1)}>
          <path d="M12 5v14M5 12h14" />
        </Stepper>
      </div>
    </div>
  );
}
